/**
 * RP4 — Layer-state effect body extracted from `Scene3DStage.tsx`.
 *
 * Re-applies layer visibility / lock / freeze whenever the layer table
 * mutates, without re-running the entire bulkLoad pass (which would
 * dispose all meshes — way too expensive for a single layer toggle).
 * Mutates the existing mesh.visible + material.opacity in place; the
 * caller is responsible for invalidating the demand frameloop after.
 *
 * # Why a dedicated module
 *
 * The standalone effect on its own is a small body (~10 lines), but
 * extracting it lets the orchestrator's effect register reduce to a
 * single function call and keeps the per-bucket reach (bucketHandles +
 * locked/hidden refs + sharedLockedLayers ref mirror) localised.
 *
 * No behavior changes — pure code move from `Scene3DStage.tsx` lines
 * 2210-2222.
 */

import * as THREE from "three";

import type { MeshKeyId } from "../batchedLayer";
import {
  applyLayerVisibility,
  type BucketRenderHandle,
  type LayerVisibilityState,
} from "../state/layerVisibility";

import type { BucketState, EntityRender } from "./SceneRefsTypes";
import { applyEntityRenderVisibility } from "./entityRenderVisibility";

interface ApplyLayerVisibilityArgs {
  /**
   * Opt 8 — consolidated bucket Map. Each entry's `handle` field holds
   * the `BucketRenderHandle` the layer-visibility reducer mutates. We
   * lift just the handles into a transient lookup so the existing
   * `applyLayerVisibility` reducer stays Map-agnostic.
   */
  buckets: Map<MeshKeyId, BucketState>;
  /**
   * PR-F2 — dimension arrow / line meshes attached directly to the
   * scene (not through the bucket handle pipeline). Hidden / frozen
   * layer state must be mirrored onto each arrow mesh's `visible`
   * flag so dim arrowheads don't render as orphans when their
   * parent dim's layer is hidden. Codex P2 on PR #63.
   */
  entityRenders?: Map<string, EntityRender>;
  /**
   * F4 (codex P1 #4) — batched-INSERT InstancedMeshes attached directly
   * to the scene by `materializeInstancedBlockInsertMesh`. The layer-
   * visibility updater walks every instance, reads its layer from
   * `userData.instanceLayers[i]`, and either restores the original
   * instance matrix (visible) or zeroes the matrix (hidden — produces
   * degenerate geometry that the GPU rasterises to nothing).
   *
   * When EVERY instance ends up hidden the mesh's `.visible` is set to
   * `false` so even the empty draw call is skipped.
   */
  instancedBlockMeshes?: ReadonlyArray<THREE.InstancedMesh>;
  layerStates: ReadonlyArray<LayerVisibilityState>;
  lockedLayersRef: { current: Set<string> };
  hiddenLayersRef: { current: Set<string> };
  sharedLockedLayersRef: { current: Set<string> };
}

/**
 * Apply the latest layer table state to the existing bucket meshes.
 * Returns `true` if any bucket was visited (caller should `invalidate()`),
 * `false` if there were no buckets to update yet.
 *
 * Used both by:
 *   - the standalone `useEffect` that reacts to layer-table changes; and
 *   - the bulkLoad effect's initial-pass at the end of bucket
 *     construction (so freshly-built meshes inherit the current layer
 *     state and don't flash visible for one frame before the dedicated
 *     effect fires).
 */
export function applyAndMirrorLayerVisibility(
  args: ApplyLayerVisibilityArgs,
): boolean {
  // F4 (codex P1 #4) — also accept "has only InstancedMeshes" as a
  // valid reason to run the apply pass; a drawing whose entirety lives
  // in batched-INSERT meshes (e.g. a survey monument grid where every
  // entity is identical-block fixture) would otherwise skip visibility
  // application entirely.
  //
  // S9b — likewise accept "has only per-entity direct meshes": a drawing that
  // is all DIMENSION / MULTILEADER / TEXT / HATCH (no line buckets, no batched
  // INSERTs) must still toggle its arrow / text / fill meshes by layer.
  // Opt 8 — project `BucketState.handle` per bucket into a lookup the
  // legacy reducer accepts. Cheap relative to the bucket count and
  // keeps `applyLayerVisibility` decoupled from the consolidated
  // bucket-state shape.
  const handles = new Map<MeshKeyId, BucketRenderHandle>();
  for (const [keyId, state] of args.buckets) {
    handles.set(keyId, state.handle);
  }
  // FIX (Codex S9b round-5 P2 — "Derive hidden layers before pending text
  // resolves"): derive + PUBLISH the hidden / locked layer sets from the layer
  // TABLE (which `applyLayerVisibility` computes purely from `layerStates`,
  // independent of the bucket handles) BEFORE the no-mesh early-out below. A
  // text-only scene — or a leaderless MLEADER whose only visible content is
  // async text — has NO buckets and NO `entityRenders` yet on the bulk-load
  // pass, but `emitTextMeshes` reads `hiddenLayersRef.current` at font
  // RESOLUTION time; skipping this derivation on the no-mesh pass left the ref
  // stale/empty and the label was created VISIBLE on an already-hidden layer.
  const result = applyLayerVisibility(handles, args.layerStates);
  args.lockedLayersRef.current = result.lockedLayers;
  args.hiddenLayersRef.current = result.hiddenLayers;
  // PR-V0c — mirror to outer Scene3DStage so the GripsDomLayer can
  // suppress grips for locked-layer entities.
  args.sharedLockedLayersRef.current = result.lockedLayers;

  const hasBuckets = args.buckets.size > 0;
  const hasInstanced =
    !!args.instancedBlockMeshes && args.instancedBlockMeshes.length > 0;
  const hasEntityRenders =
    !!args.entityRenders && args.entityRenders.size > 0;
  // Nothing to APPLY visibility to yet (no meshes). The refs above are now
  // populated so an async text build resolves against the correct hidden set;
  // return `false` so the caller skips an `invalidate()` — no visible change
  // this pass (the async build invalidates itself when it resolves).
  if (!hasBuckets && !hasInstanced && !hasEntityRenders) return false;
  // PR-F2 / S9b — apply hidden-layer state to per-entity DIRECT meshes attached
  // straight to the scene (not through the bucket handle pipeline): dimension /
  // multileader arrow decorators (codex P2 on PR #63 a0c136f), synthesised
  // MLEADER / dimension text, and hatch / wipeout fills. Without this such
  // meshes survive as orphans on hidden layers. `applyEntityRenderVisibility`
  // recomputes the composite (layer AND lod) so this pass does NOT clobber a
  // concurrent LOD-cull hide (S9b P2 — direct meshes have two hide sources).
  if (args.entityRenders) {
    const hidden = result.hiddenLayers;
    for (const render of args.entityRenders.values()) {
      applyEntityRenderVisibility(render, hidden);
    }
  }
  // F4 (codex P1 #4) — apply hidden-layer state to batched-INSERT
  // InstancedMeshes. Each mesh stores `userData.instanceLayers[i]`
  // and `userData.instanceMatricesOriginal[i]`; we rewrite the
  // per-instance matrix to either zero (hidden) or the saved
  // original (visible). `mesh.visible` is also toggled when every
  // instance ends up hidden so the draw call itself is skipped.
  if (args.instancedBlockMeshes && args.instancedBlockMeshes.length > 0) {
    const hidden = result.hiddenLayers;
    for (const mesh of args.instancedBlockMeshes) {
      const userData = mesh.userData as {
        instanceLayers?: ReadonlyArray<string>;
        instanceMatricesOriginal?: ReadonlyArray<THREE.Matrix4>;
      };
      const layers = userData.instanceLayers;
      const originals = userData.instanceMatricesOriginal;
      if (!layers || !originals) continue;
      let anyVisible = false;
      let mutated = false;
      for (let i = 0; i < layers.length; i++) {
        const layerHidden = hidden.has(layers[i] ?? "0");
        if (layerHidden) {
          mesh.setMatrixAt(i, ZERO_MATRIX);
          mutated = true;
        } else {
          const original = originals[i];
          if (original) {
            mesh.setMatrixAt(i, original);
            mutated = true;
          }
          anyVisible = true;
        }
      }
      mesh.visible = anyVisible;
      if (mutated) mesh.instanceMatrix.needsUpdate = true;
    }
  }
  return true;
}

/**
 * Shared zero `THREE.Matrix4` used to collapse hidden instances.
 * Sharing one instance across all hidden-mesh writes avoids allocating
 * per-frame. All-zeros matrix produces degenerate geometry (every
 * transformed vertex collapses to the origin) so the GPU rasterises
 * nothing for the instance — equivalent to "hidden" without disturbing
 * the InstancedMesh's instance count.
 */
const ZERO_MATRIX = new THREE.Matrix4().set(
  0, 0, 0, 0,
  0, 0, 0, 0,
  0, 0, 0, 0,
  0, 0, 0, 0,
);
